import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider, useBranding } from "@/context/BrandingContext";
import { LogoMark } from "@/components/logo/AetriaLogo";
import { isTenantSurface } from "@/lib/api";
import SplashScreen from "@/components/SplashScreen";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import DocumentsPage from "@/pages/DocumentsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import SecurityConsolePage from "@/pages/SecurityConsolePage";
import SkillsIntelligencePage from "@/pages/SkillsIntelligencePage";
import LearningPage from "@/pages/LearningPage";
import OrgIntelligencePage from "@/pages/OrgIntelligencePage";
import PayrollPage from "@/pages/PayrollPage";
import MyPayslipsPage from "@/pages/MyPayslipsPage";
import PerformancePage from "@/pages/PerformancePage";
import OpenQueryPage from "@/pages/OpenQueryPage";
import CloudSettingsPage from "@/pages/CloudSettingsPage";
import RolesPage from "@/pages/RolesPage";
import SuspendedPage from "@/pages/SuspendedPage";
import PlatformConsolePage from "@/pages/PlatformConsolePage";
import LandingPage from "@/pages/LandingPage";
import SignupPage from "@/pages/SignupPage";
import BillingPage from "@/pages/BillingPage";
import ManualPage from "@/pages/ManualPage";
import BrandingSettingsPage from "@/pages/BrandingSettingsPage";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="pulse-dot"><LogoMark size={56} /></div>
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  const { noTenant } = useBranding();
  const location = useLocation();
  if (noTenant) return <LandingPage />;
  if (user === null) return <FullLoader />;
  if (!user) {
    // Root of the base/preview domain -> product showcase; tenant subdomains
    // (and explicit ?tenant= workspaces) go straight to their branded login.
    if (location.pathname === "/" && !isTenantSurface()) return <LandingPage />;
    return <Navigate to="/login" replace />;
  }
  return children;
}

function MeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.employee_id ? `/employees/${user.employee_id}` : "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
      <SplashScreen />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/suspended" element={<SuspendedPage />} />
          <Route path="/platform" element={<PlatformConsolePage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/employees" element={<EmployeesPage />} />
            <Route path="/employees/:id" element={<EmployeeDetailPage />} />
            <Route path="/me" element={<MeRedirect />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/skills" element={<SkillsIntelligencePage />} />
            <Route path="/org" element={<OrgIntelligencePage />} />
            <Route path="/learning" element={<LearningPage />} />
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/payslips" element={<MyPayslipsPage />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/query" element={<OpenQueryPage />} />
            <Route path="/cloud" element={<CloudSettingsPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/admin/branding" element={<BrandingSettingsPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/manual" element={<ManualPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/security" element={<SecurityConsolePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
